/**
 * Team Design — suggested color theme from the uploaded logo.
 *
 * After the shopper uploads a team logo, run the logo image through
 * `window.TruetalePalette` (the `@3d/palette` UMD bundle loaded in
 * theme.liquid) and insert a "Suggested from your logo" swatch into the
 * Quick Customization color grid (`#qc-swatches`), positioned right after
 * the colorful pseudo-swatch so it's the FIRST real theme the shopper
 * sees. The swatch is rendered with the same `data-primary/secondary/
 * tertiary/accent` attributes the existing click handler in
 * team-design.banner-swatches.js already understands — no changes needed
 * to that file's click logic.
 *
 * Sources of logo state we react to:
 *   - `teamLogoUploaded` event (fresh upload)
 *   - `teamLogoRemoved` event (clear the suggested swatch)
 *   - boot-time `TeamDesignStorage.get().logo` (persisted logo across visits)
 *
 * The grid is re-rendered by banner-swatches.js after the API call resolves
 * (and may be re-rendered again on filter / pagination changes), so we
 * watch it with a MutationObserver and re-insert the suggested swatch
 * whenever it disappears from the DOM but `state.suggestedTheme` is set.
 */
(function () {
  'use strict';

  var SUGGESTED_SWATCH_CLASS = 'qc-swatch--suggested';
  var SUGGESTED_FRESH_CLASS = 'qc-swatch--suggested-fresh';
  var PALETTE_WAIT_TIMEOUT_MS = 5000;
  var PALETTE_WAIT_POLL_MS = 50;
  var LOG_PREFIX = '[team-design.logo-colors]';

  // Flip to true to get step-by-step logs in devtools while debugging why a
  // particular logo / collection / browser doesn't produce a swatch. Off on
  // prod so `log()` calls below are no-ops; real failures still `console.warn`.
  var DEBUG = false;
  function log() {
    if (!DEBUG || !window.console || !console.log) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.log.apply(console, args);
  }

  // Inject the suggestion-only styles once. Kept inline so the new
  // visual treatment ships with the script — no edits to the
  // quick-customization-desktop/-mobile stylesheets needed.
  function ensureStyleInjected() {
    if (document.getElementById('qc-suggested-style')) return;
    var s = document.createElement('style');
    s.id = 'qc-suggested-style';
    // One-shot pulse when the swatch first appears (logo just uploaded,
    // or persisted-logo restored on page load). The class is removed
    // after the animation finishes so a future re-insert can re-trigger
    // it by re-adding the class. No badge / ring — the animation alone
    // signals the swatch's specialness, then it sits flush with its
    // siblings once it stops moving.
    s.textContent =
      '@keyframes qcSuggestedPulse{' +
        '0%,100%{transform:scale(1);}' +
        '50%{transform:scale(1.12);}' +
      '}' +
      '.' + SUGGESTED_FRESH_CLASS + '{' +
        'animation:qcSuggestedPulse 1.4s ease-in-out 2;' +
      '}';
    document.head.appendChild(s);
  }

  var state = {
    // Cached so a grid re-render can re-insert the swatch without
    // re-running extraction. Reset to null on teamLogoRemoved.
    suggestedTheme: null,
  };

  function el(id) {
    return document.getElementById(id);
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;');
  }

  // Lazy-load the externally-hosted `@3d/palette` bundle the first time
  // we actually need it. The bundle is *not* loaded in theme.liquid (it
  // used to be) — most shoppers never upload a logo, so eagerly fetching
  // the bundle for everyone was wasted bandwidth.
  //
  // The promise is memoized so concurrent extractions share a single
  // load. On script load the UMD bundle attaches `window.TruetalePalette`;
  // we resolve from `script.onload`, falling back to a poll in case
  // onload fires before the global is fully assigned on some browsers.
  var palettePromise = null;
  function loadPaletteScript() {
    if (window.TruetalePalette) {
      log('palette already on window');
      return Promise.resolve(window.TruetalePalette);
    }
    if (palettePromise) return palettePromise;

    palettePromise = new Promise(function (resolve, reject) {
      var src = (window.APP_CONFIG && window.APP_CONFIG.endpoints.palette) || 'https://scripts.squadmojo-dev.com/palette/palette.js';
      log('loading palette script:', src);

      var script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.onload = function () {
        if (window.TruetalePalette) {
          log('palette ready');
          return resolve(window.TruetalePalette);
        }
        // Edge case: onload fired but the UMD global assignment hasn't
        // settled yet. Poll briefly before giving up.
        var start = Date.now();
        var id = setInterval(function () {
          if (window.TruetalePalette) {
            clearInterval(id);
            resolve(window.TruetalePalette);
          } else if (Date.now() - start > PALETTE_WAIT_TIMEOUT_MS) {
            clearInterval(id);
            reject(new Error('palette loaded but TruetalePalette global never appeared'));
          }
        }, PALETTE_WAIT_POLL_MS);
      };

      script.onerror = function () {
        // Reset the memo so a future logo upload retries the load —
        // the first failure may have been a transient CDN hiccup.
        palettePromise = null;
        reject(new Error('palette script failed to load — is ' + src + ' reachable?'));
      };

      document.head.appendChild(script);
    });

    return palettePromise;
  }

  // Render an inline-styled swatch button matching the shape `renderSwatches`
  // in team-design.banner-swatches.js produces: top row = primary (flex 2),
  // bottom row split = secondary + tertiary. The same `bindSwatchClicks`
  // listener picks this up via `.qc-swatch` + the data attributes.
  //
  // Deliberately emits NO `data-theme-code` — the suggested theme is not a
  // registered preset, so it must travel to the PDP as a *custom* theme.
  // The card-product.liquid URL writer (`appendBannerParams`) routes themes
  // by code: a truthy code → forward `CT=<code>` only (PDP looks it up as a
  // preset); a falsy code with role colors → forward each role color so the
  // PDP can rebuild the exact custom palette. Setting any code here (even a
  // placeholder like `__logo_suggested__`) makes the PDP try a lookup that
  // can never resolve, dropping the colors entirely.
  function suggestedSwatchHtml(theme) {
    var primary = theme.primaryColor || '#ccc';
    var secondary = theme.secondaryColor || primary;
    var tertiary = theme.tertiaryColor || theme.accentColor || primary;
    return (
      '<button type="button" class="qc-swatch ' + SUGGESTED_SWATCH_CLASS + '"' +
      ' data-primary="'   + escapeAttr(theme.primaryColor)   + '"' +
      ' data-secondary="' + escapeAttr(theme.secondaryColor) + '"' +
      ' data-tertiary="'  + escapeAttr(theme.tertiaryColor)  + '"' +
      ' data-accent="'    + escapeAttr(theme.accentColor)    + '"' +
      ' aria-label="Suggested from your logo"' +
      ' title="Suggested from your logo">' +
        '<span class="qc-swatch__row qc-swatch__row--top" style="background-color:' + escapeAttr(primary) + ';"></span>' +
        '<span class="qc-swatch__row qc-swatch__row--split">' +
          '<span style="background-color:' + escapeAttr(secondary) + ';"></span>' +
          '<span style="background-color:' + escapeAttr(tertiary)  + ';"></span>' +
        '</span>' +
      '</button>'
    );
  }

  // Insert/refresh the suggested swatch as the first child after the
  // colorful pseudo-swatch (which always sits at index 0). When `fresh`
  // is true (the suggestion was just produced — fresh upload or persisted-
  // logo restore) the swatch animates briefly to draw the shopper's eye.
  // Plain grid-re-render re-inserts (via the MutationObserver) pass
  // `fresh: false` so the animation only plays at the moment of intent.
  function refreshSuggestedSwatchInDom(fresh) {
    var container = el('qc-swatches');
    if (!container) return;

    var existing = container.querySelector('.' + SUGGESTED_SWATCH_CLASS);
    if (!state.suggestedTheme) {
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      return;
    }

    // Rebuild from the stashed theme so a re-render with new colors
    // doesn't leave stale data-* attributes behind.
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var html = suggestedSwatchHtml(state.suggestedTheme);
    var colorful = container.querySelector('.qc-swatch--colorful');
    if (colorful && colorful.insertAdjacentHTML) {
      colorful.insertAdjacentHTML('afterend', html);
      log('swatch inserted after colorful');
    } else {
      // No colorful swatch yet (grid hasn't been rendered by
      // banner-swatches.js). Prepend so we still occupy index 0 — the
      // MutationObserver below will re-run once the API render fires and
      // we'll move into the second slot.
      container.insertAdjacentHTML('afterbegin', html);
      log('swatch inserted at start (no colorful swatch yet)');
    }

    if (fresh) {
      var inserted = container.querySelector('.' + SUGGESTED_SWATCH_CLASS);
      if (inserted) {
        inserted.classList.add(SUGGESTED_FRESH_CLASS);
        // Strip the class once the pulse finishes so a later re-insert
        // can re-trigger it cleanly. The animation runs `2 * 1.4s = 2.8s`
        // — `animationend` fires per iteration, so we use a timeout to
        // avoid hooking the listener and counting iterations.
        setTimeout(function () {
          inserted.classList.remove(SUGGESTED_FRESH_CLASS);
        }, 3000);
      }
    }
  }

  // Fetch the image bytes and convert to a base64 data URL. We do this
  // *before* handing the URL to the palette so the palette's internal
  // `new Image()` (with `crossOrigin='anonymous'`) never has to negotiate
  // CORS for canvas pixel reads — Shopify storefronts are on a different
  // origin than the media S3 bucket, and even when fetch() succeeds the
  // <img> path can still taint the canvas under some bucket configs.
  // Data URLs are same-origin by definition and bypass that entire class
  // of failure. Same trick the design-tools `logo-tab.tsx` uses.
  function fetchAsDataUrl(url) {
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('logo fetch failed: ' + res.status);
        return res.blob();
      })
      .then(function (blob) {
        return new Promise(function (resolve, reject) {
          var reader = new FileReader();
          reader.onload = function () { resolve(reader.result); };
          reader.onerror = function () { reject(reader.error); };
          reader.readAsDataURL(blob);
        });
      });
  }

  // Run extraction against an image URL (data URL or http URL — http
  // URLs go through `fetchAsDataUrl` first to dodge CORS canvas tainting).
  function extractFromUrl(url) {
    if (!url) {
      state.suggestedTheme = null;
      refreshSuggestedSwatchInDom(false);
      return;
    }
    var source = /^data:/.test(url) ? Promise.resolve(url) : fetchAsDataUrl(url);
    // Kick off the palette script load and the image fetch in parallel —
    // first call to `loadPaletteScript` injects the <script> tag, all
    // future calls await the memoized promise.
    Promise.all([loadPaletteScript(), source])
      .then(function (results) {
        var api = results[0];
        var dataUrl = results[1];
        log('image fetched, running extraction');
        return api.extractPalette(dataUrl).then(api.paletteToTheme);
      })
      .then(function (theme) {
        log('extracted theme:', theme);
        state.suggestedTheme = theme;
        // `true` plays the one-shot pulse — extraction completed just now,
        // so this is the right moment to draw the shopper's eye.
        refreshSuggestedSwatchInDom(true);
      })
      .catch(function (err) {
        // Don't break the page on a failed extract — but log so a slow
        // CDN, a bad CORS config, or a decode error is at least visible
        // in devtools when QA looks for it.
        if (window.console && console.warn) {
          console.warn(LOG_PREFIX, 'extraction failed:', err);
        }
        state.suggestedTheme = null;
        refreshSuggestedSwatchInDom(false);
      });
  }

  // Watch the swatch container so a grid re-render (banner-swatches.js
  // rewrites `innerHTML` after fetching themes) can't strand a suggested
  // swatch we already extracted. We re-insert from the cached theme each
  // time the children change AND the suggested swatch isn't present.
  function startGridObserver() {
    var container = el('qc-swatches');
    if (!container) return;
    var observer = new MutationObserver(function () {
      if (!state.suggestedTheme) return;
      if (container.querySelector('.' + SUGGESTED_SWATCH_CLASS)) return;
      // `false` — this is a grid re-render replacing a swatch that was
      // already shown; don't replay the attention-grabbing pulse.
      refreshSuggestedSwatchInDom(false);
    });
    observer.observe(container, { childList: true });
  }

  // ─── Upload events ────────────────────────────────────────────────────────
  document.addEventListener('teamLogoUploaded', function (e) {
    var result = e && e.detail;
    var url = result && result.urls && (result.urls.medium || result.urls.original);
    log('teamLogoUploaded → url:', url);
    extractFromUrl(url);
  });

  document.addEventListener('teamLogoRemoved', function () {
    log('teamLogoRemoved');
    state.suggestedTheme = null;
    refreshSuggestedSwatchInDom(false);
  });

  // ─── Init: restore from persisted logo if present ────────────────────────
  function init() {
    log('init — qc-swatches container:', el('qc-swatches'));
    ensureStyleInjected();
    startGridObserver();

    var storage = window.TeamDesignStorage;
    if (!storage || !storage.get) {
      log('no TeamDesignStorage; persisted-logo path skipped');
      return;
    }
    var saved = storage.get();
    var url = saved && saved.logo && saved.logo.urls &&
              (saved.logo.urls.medium || saved.logo.urls.original);
    if (url) {
      log('restoring persisted logo →', url);
      extractFromUrl(url);
    } else {
      log('no persisted logo');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
